import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { LocationBoxPlacement } from "../LocationLayout/types";
import {
  locationPartRailTopY,
  locationStackTopY,
} from "../LocationLayout/metrics";
import { LOCATION_BOX_STEP_PX } from "../LocationMarker/stack";
import { locationPlacementAt, locationPlacementPlan } from "../LocationPlacement/plan";
import {
  branchForLocationRun,
  forkTravelerFollowActive,
  forkTravelerStageAnchor,
  branchForSiteRun,
  siteGroupsMidpointDx,
} from "./siteAnchor";
import { partCastDx } from "../LocationLayout/partCastDx";
import type { LocationRun } from "./runs";
import { matchingRailRun } from "./runs";
import { partRunPhase } from "../../rail/PartRunPhase/phase";

export function isPartLocationRun(run: LocationRun): boolean {
  return run.runId.includes(":");
}

export { branchIdForPartRun } from "../../rail/PartRunPhase/phase";

function castFollowAnchor(
  layout: BookTimelineLayout,
  run: LocationRun,
  placement: LocationBoxPlacement,
  playheadMs: number,
  castRole?: "primary" | "secondary",
): { anchorX: number; topY: number } {
  const branchRun = matchingRailRun(run, layout.rail.branches, layout.beats);
  const siteY = locationStackTopY(layout.rail, branchRun);
  const topY = siteY
    + (castRole === "secondary" ? LOCATION_BOX_STEP_PX : 0);
  let anchorX = placement.anchorX;
  if (isPartLocationRun(run)) {
    anchorX += partCastDx(layout, run, branchRun, playheadMs);
  } else {
    anchorX += siteGroupsMidpointDx(branchRun, layout.rail, playheadMs);
  }
  return { anchorX, topY };
}

function railSlotAnchor(
  layout: BookTimelineLayout,
  run: LocationRun,
  placement: LocationBoxPlacement,
  playheadMs: number,
): { anchorX: number; topY: number } {
  const branchRun = matchingRailRun(run, layout.rail.branches, layout.beats);
  const partPhase = isPartLocationRun(run)
    ? partRunPhase(layout.rail, run, playheadMs)
    : undefined;
  if (partPhase?.kind === "upcoming" || partPhase?.kind === "parked") {
    const freezeMs = partPhase.kind === "upcoming"
      ? partPhase.railStartMs
      : partPhase.handoffMs;
    let anchorX = placement.anchorX;
    if (partPhase.kind === "upcoming") {
      anchorX += partCastDx(layout, run, branchRun, freezeMs);
    }
    return {
      anchorX,
      topY: locationPartRailTopY(layout.rail, branchRun),
    };
  }
  if (!isPartLocationRun(run) && playheadMs < run.startMs) {
    // Wait at the height it will have under the cast, so the handoff lines up.
    if (branchForSiteRun(run, layout.rail) !== undefined) {
      return {
        anchorX: placement.anchorX
          + siteGroupsMidpointDx(branchRun, layout.rail, run.startMs),
        topY: locationStackTopY(layout.rail, branchRun),
      };
    }
  }
  return { anchorX: placement.anchorX, topY: placement.topY };
}

function forkTravelerBoxAnchor(
  layout: BookTimelineLayout,
  run: LocationRun,
  playheadMs: number,
  viewportWidth: number,
): { anchorX: number; topY: number } | null {
  if (!forkTravelerFollowActive(layout.rail, run, playheadMs)) {
    return null;
  }
  const branch = branchForLocationRun(run, layout.rail);
  if (branch === undefined) {
    return null;
  }
  const topYDelta = isPartLocationRun(run) ? LOCATION_BOX_STEP_PX : 0;
  return forkTravelerStageAnchor(
    layout.rail,
    branch,
    playheadMs,
    viewportWidth,
    layout.totalDurationMs,
    topYDelta,
  );
}

/** Stage anchor from precomputed cast vs rail segments plus rail geometry. */
export function locationBoxAnchor(
  layout: BookTimelineLayout,
  run: LocationRun,
  placement: LocationBoxPlacement,
  playheadMs: number,
  viewportWidth = 1200,
): { anchorX: number; topY: number } {
  const traveler = forkTravelerBoxAnchor(
    layout,
    run,
    playheadMs,
    viewportWidth,
  );
  if (traveler !== null) {
    return traveler;
  }
  const segment = locationPlacementAt(
    locationPlacementPlan(layout),
    run.runId,
    playheadMs,
  );
  if (segment?.mode === "cast-column") {
    return castFollowAnchor(
      layout,
      run,
      placement,
      playheadMs,
      segment.castRole,
    );
  }
  return railSlotAnchor(layout, run, placement, playheadMs);
}
